import villasMeta from "../data/villasMeta";
import { BUSINESS, BUSINESS_IDS, SITE_URL } from "../data/businessInfo";

const SCHEMA_CONTEXT = "https://schema.org";
const BUSINESS_DESCRIPTION = BUSINESS.description;

function toAbsoluteUrl(pathOrUrl) {
  if (!pathOrUrl) return undefined;
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  return `${SITE_URL}${pathOrUrl.startsWith("/") ? pathOrUrl : `/${pathOrUrl}`}`;
}

function parseMaxGuests(numGuests) {
  if (!numGuests) return undefined;
  const match = String(numGuests).match(/\d+/g);
  if (!match) return undefined;
  return Math.max(...match.map(Number));
}

function parseRoomCount(nightStay) {
  const match = String(nightStay || "").match(/T(\d+)/i);
  return match ? Number(match[1]) : undefined;
}

function buildPostalAddress() {
  return {
    "@type": "PostalAddress",
    ...BUSINESS.address,
  };
}

function buildGeo() {
  return {
    "@type": "GeoCoordinates",
    ...BUSINESS.geo,
  };
}

function buildAmenityFeatures() {
  return BUSINESS.amenities.map((name) => ({
    "@type": "LocationFeatureSpecification",
    name,
    value: true,
  }));
}

function buildLodgingBusiness() {
  return {
    "@type": ["LodgingBusiness", "LocalBusiness"],
    "@id": BUSINESS_IDS.lodging,
    name: BUSINESS.name,
    url: SITE_URL,
    image: BUSINESS.image,
    logo: BUSINESS.image,
    description: BUSINESS_DESCRIPTION,
    email: BUSINESS.email,
    telephone: BUSINESS.phones,
    priceRange: BUSINESS.priceRange,
    checkinTime: BUSINESS.checkinTime,
    checkoutTime: BUSINESS.checkoutTime,
    address: buildPostalAddress(),
    geo: buildGeo(),
    amenityFeature: buildAmenityFeatures(),
    hasMap:
      "https://www.google.com/maps/place/Tapada+de+S%C3%A3o+Domingos/@41.0727025,-8.5097015,17z",
    containsPlace: villasMeta.map((villa) => ({
      "@id": `${SITE_URL}/villa/${villa.slug}#accommodation`,
    })),
  };
}

function buildOrganization() {
  return {
    "@type": "Organization",
    "@id": BUSINESS_IDS.organization,
    name: BUSINESS.name,
    url: SITE_URL,
    logo: BUSINESS.image,
    description: BUSINESS_DESCRIPTION,
    email: BUSINESS.email,
    telephone: BUSINESS.phones,
    address: buildPostalAddress(),
  };
}

function buildWebsite() {
  return {
    "@type": "WebSite",
    "@id": BUSINESS_IDS.website,
    url: SITE_URL,
    name: BUSINESS.name,
    description: BUSINESS_DESCRIPTION,
    inLanguage: ["pt-PT", "en"],
    publisher: { "@id": BUSINESS_IDS.organization },
  };
}

function buildWebPage({ path = "/", name, description, pageType = "WebPage" }) {
  const pageUrl = toAbsoluteUrl(path);

  return {
    "@type": pageType,
    "@id": `${pageUrl}#webpage`,
    url: pageUrl,
    name: name || BUSINESS.name,
    description: description || BUSINESS_DESCRIPTION,
    isPartOf: { "@id": BUSINESS_IDS.website },
    about: { "@id": BUSINESS_IDS.lodging },
    inLanguage: ["pt-PT", "en"],
  };
}

function buildVillaAccommodation(villa, imageUrl) {
  const villaUrl = `${SITE_URL}/villa/${villa.slug}`;
  const maxGuests = parseMaxGuests(villa.numGuests);
  const numberOfRooms = parseRoomCount(villa.nightStay);

  return {
    "@type": ["Accommodation", "VacationRental", "House"],
    "@id": `${villaUrl}#accommodation`,
    name: villa.title,
    description: villa.shortDescription,
    url: villaUrl,
    image: imageUrl ? toAbsoluteUrl(imageUrl) : BUSINESS.image,
    containedInPlace: { "@id": BUSINESS_IDS.lodging },
    ...(numberOfRooms ? { numberOfRooms } : {}),
    ...(maxGuests
      ? {
          occupancy: {
            "@type": "QuantitativeValue",
            maxValue: maxGuests,
            unitText: "guests",
          },
        }
      : {}),
    amenityFeature: villa.facilities.map((name) => ({
      "@type": "LocationFeatureSpecification",
      name,
      value: true,
    })),
    offers: {
      "@type": "Offer",
      url: `${SITE_URL}/reservations`,
      priceCurrency: "EUR",
      availability: "https://schema.org/InStock",
      ...(villa.price?.low && villa.price?.high
        ? {
            priceSpecification: {
              "@type": "PriceSpecification",
              minPrice: villa.price.low,
              maxPrice: villa.price.high,
              priceCurrency: "EUR",
              unitText: "night",
            },
          }
        : {}),
    },
  };
}

function buildServiceCatalog() {
  return {
    "@type": "OfferCatalog",
    name: BUSINESS_DESCRIPTION,
    description: BUSINESS_DESCRIPTION,
    itemListElement: villasMeta.map((villa, index) => ({
      "@type": "ListItem",
      position: index + 1,
      item: {
        "@type": "Offer",
        name: villa.title,
        description: villa.shortDescription,
        url: `${SITE_URL}/villa/${villa.slug}`,
      },
    })),
  };
}

export function buildHomeStructuredData() {
  return {
    "@context": SCHEMA_CONTEXT,
    "@graph": [
      buildOrganization(),
      buildLodgingBusiness(),
      buildWebsite(),
      buildWebPage({
        path: "/",
        name: `${BUSINESS.name} — ${BUSINESS_DESCRIPTION}`,
        description: BUSINESS_DESCRIPTION,
      }),
      buildServiceCatalog(),
      ...villasMeta.map((villa) => buildVillaAccommodation(villa)),
    ],
  };
}

export function buildVillaStructuredData(villa, imageUrl) {
  if (!villa) return buildHomeStructuredData();

  return {
    "@context": SCHEMA_CONTEXT,
    "@graph": [
      buildOrganization(),
      buildLodgingBusiness(),
      buildWebsite(),
      buildWebPage({
        path: `/villa/${villa.slug}`,
        name: `${villa.title} | ${BUSINESS.name}`,
        description: villa.descriptionBlocks?.house || villa.shortDescription,
        pageType: "ItemPage",
      }),
      buildVillaAccommodation(villa, imageUrl),
    ],
  };
}

export function buildReservationsStructuredData() {
  return {
    "@context": SCHEMA_CONTEXT,
    "@graph": [
      buildOrganization(),
      buildLodgingBusiness(),
      buildWebsite(),
      buildWebPage({
        path: "/reservations",
        name: `Reservations | ${BUSINESS.name}`,
        description: `Request a reservation at ${BUSINESS.name}, ${BUSINESS_DESCRIPTION}.`,
        pageType: "ContactPage",
      }),
    ],
  };
}

export function buildRegionStructuredData(path = "/region") {
  return {
    "@context": SCHEMA_CONTEXT,
    "@graph": [
      buildOrganization(),
      buildLodgingBusiness(),
      buildWebsite(),
      buildWebPage({
        path,
        name: `Northern Portugal Region | ${BUSINESS.name}`,
        description:
          "Explore Northern Portugal from Tapada de São Domingos — Porto, the Douro Valley, and the surrounding region.",
      }),
    ],
  };
}

export function buildStructuredDataForPath(pathname, villa, villaImageUrl) {
  if (pathname.startsWith("/villa/") && villa) {
    return buildVillaStructuredData(villa, villaImageUrl);
  }

  if (pathname === "/reservations") {
    return buildReservationsStructuredData();
  }

  if (pathname === "/region" || pathname === "/regiao") {
    return buildRegionStructuredData(pathname);
  }

  return buildHomeStructuredData();
}
